/**
 * Store-hours formatting + open/closed status.
 *
 * Times are "HH:MM" 24-hour strings (as stored on the Location). The current
 * open/closed status is evaluated in Mountain Time, see CONVENTIONS.md ›
 * Timezone. Hours that wrap past midnight (e.g. 09:00–02:00) are handled.
 */

/** Format a "HH:MM" 24-hour string into a 12-hour label, e.g. "09:00" -> "9 AM". */
export function formatTime12h(hhmm: string | null | undefined): string | null {
  if (!hhmm) return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return min === 0 ? `${h12} ${period}` : `${h12}:${String(min).padStart(2, '0')} ${period}`;
}

/** "9 AM – 2 AM daily" from a store's open/close times, or a fallback. */
export function formatStoreHours(
  openTime: string | null | undefined,
  closeTime: string | null | undefined,
): string {
  const open = formatTime12h(openTime);
  const close = formatTime12h(closeTime);
  if (open && close) return `${open} – ${close} daily`;
  return 'Hours not available';
}

/** "Daily: 9 AM – 2 AM" from a store's open/close times, or a fallback. */
export function formatDailyHours(
  openTime?: string | null,
  closeTime?: string | null,
): string {
  const open = formatTime12h(openTime);
  const close = formatTime12h(closeTime);
  if (open && close) return `Daily: ${open} – ${close}`;
  return 'Hours not available';
}

/** Minutes since midnight for a "HH:MM" string, or null. */
function toMinutes(hhmm: string | null | undefined): number | null {
  if (!hhmm) return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

export type StoreStatus = { open: boolean; label: string };

/**
 * Whether the store is open right now (Mountain Time) given its daily hours,
 * with a label, "Closes 2 AM" when open, "Opens 9 AM" when closed. Handles
 * hours that wrap past midnight. Returns null when hours are not configured.
 */
export function getStoreStatus(
  openTime: string | null | undefined,
  closeTime: string | null | undefined,
): StoreStatus | null {
  const openMin = toMinutes(openTime);
  const closeMin = toMinutes(closeTime);
  if (openMin == null || closeMin == null) return null;

  // Current time-of-day in Mountain Time, as minutes since midnight.
  const nowMin =
    toMinutes(
      new Date().toLocaleTimeString('en-GB', {
        timeZone: 'America/Edmonton',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }),
    ) ?? 0;

  // closeMin <= openMin means the store closes after midnight.
  const wrapsMidnight = closeMin <= openMin;
  const isOpen = wrapsMidnight
    ? nowMin >= openMin || nowMin < closeMin
    : nowMin >= openMin && nowMin < closeMin;

  return isOpen
    ? { open: true, label: `Closes ${formatTime12h(closeTime)}` }
    : { open: false, label: `Opens ${formatTime12h(openTime)}` };
}
